import React, { FC, useCallback, useMemo, useState } from 'react';
import { connect } from 'react-redux';
import { Button, Form, Input, InputNumber, Modal, message } from 'antd';
import { CloseOutlined, DeleteOutlined, PlusOutlined, UploadOutlined } from '@ant-design/icons';
import { spotPurchasesRestCrudThunksForSupplier } from '../../thunks/spot_purchases_thunks';
import OWUpload from '../OWUpload';
import {
	getConfigValueDefaultingToTrue,
	getCurrency,
	nullSafeGet,
	nullSafeGetOrElse,
} from '../../utils/DataAccessUtils';
import { getFileLinkFromString } from '../../utils/FileAccessUtils';
import './RecordSpotPurchaseModal.less';

const DEFAULT_UOM_LABEL = 'per unit';
const ACCEPTED_RECEIPT_TYPES = '.pdf,.png,.jpg,.jpeg';
const ACCEPTED_RECEIPT_MIME_TYPES = ['application/pdf', 'image/png', 'image/jpeg'];
const MAX_RECEIPT_SIZE_MB = 10;

const SAVE_BUTTON_CLASSES = `!h-10 !min-w-[114px] !rounded-sm !border !text-base !leading-6 !text-white !shadow-[0_2px_0_rgba(0,0,0,0.04)]`;

interface DraftItem {
	id?: number;
	description: string;
	quantity: number;
	unitCost: number;
	uomLabel: string;
}

const mapServerItemsToDrafts = (items: any[]): DraftItem[] =>
	(items || []).map((item) => ({
		id: item.id,
		description: item.name || item.description || '',
		quantity: Number(item.quantity) || 1,
		unitCost: Number(item.unitCost) || 0,
		uomLabel: item.perUnit || item.uomLabel || '',
	}));

interface RecordSpotPurchaseModalProps {
	visible: boolean;
	workOrder: any;
	spotPurchase?: any;
	currentUser: any;
	companyConfig: any;
	onCancel: () => void;
	onSuccess: () => void;
	create: (entity: any) => Promise<any>;
	update: (entity: any) => Promise<any>;
}

const RecordSpotPurchaseModal: FC<RecordSpotPurchaseModalProps> = ({
	visible,
	workOrder,
	spotPurchase,
	currentUser,
	companyConfig,
	onCancel,
	onSuccess,
	create,
	update,
}) => {
	const [form] = Form.useForm();
	const isUpdate = !!nullSafeGet('id', spotPurchase);

	const currencyId = nullSafeGetOrElse('facility.currencyId', currentUser, 'USD');
	const currency = getCurrency({ currencyId: currencyId, currentUser });
	const currencyCode = nullSafeGetOrElse('id', currency, 'USD');
	const currencySymbol = nullSafeGetOrElse('symbol', currency, '$');

	const initialDrafts = useMemo(
		() => mapServerItemsToDrafts(nullSafeGetOrElse('items', spotPurchase, [])),
		[spotPurchase]
	);
	const initialTaxAmount = useMemo(() => {
		const raw = nullSafeGet('taxAmount', spotPurchase);
		return raw === null || raw === undefined ? null : Number(raw);
	}, [spotPurchase]);

	const initialValues = useMemo(
		() => ({
			items:
				initialDrafts.length > 0
					? initialDrafts
					: [{ description: '', quantity: 1, uomLabel: DEFAULT_UOM_LABEL, unitCost: 0 }],
		}),
		[initialDrafts]
	);

	const watchedItems = Form.useWatch('items', form) || initialValues.items;

	const [hasTax, setHasTax] = useState<boolean>(initialTaxAmount !== null);
	const [taxAmount, setTaxAmount] = useState<number | null>(initialTaxAmount);
	const [receiptUrl, setReceiptUrl] = useState<string | null>(
		nullSafeGetOrElse('receiptUrl', spotPurchase, null)
	);
	const [receiptFileName, setReceiptFileName] = useState<string | null>(
		nullSafeGetOrElse('receiptFileName', spotPurchase, null)
	);
	const [uploading, setUploading] = useState(false);
	const [submitting, setSubmitting] = useState(false);
	const [receiptError, setReceiptError] = useState(false);

	const receiptViewUrl = useMemo(() => {
		if (!receiptUrl) return null;
		if (/^https?:\/\//i.test(receiptUrl)) return receiptUrl;
		return getFileLinkFromString('supplier')(receiptUrl);
	}, [receiptUrl]);

	const sumItems = useCallback(
		(items: any[]) =>
			(items || []).reduce(
				(acc: number, entry: any) =>
					acc +
					(Number(nullSafeGet('quantity', entry)) || 0) *
						(Number(nullSafeGet('unitCost', entry)) || 0),
				0
			),
		[]
	);

	const [totalDisplay, setTotalDisplay] = useState<number>(
		() => sumItems(initialValues.items) + (initialTaxAmount || 0)
	);

	const handleValuesChange = useCallback(
		(_changed: any, allValues: any) => {
			setTotalDisplay(sumItems(allValues.items) + (Number(taxAmount) || 0));
		},
		[sumItems, taxAmount]
	);

	const handleTaxChange = useCallback(
		(value: number | string | null | undefined) => {
			const next = value === null || value === undefined || value === '' ? null : Number(value);
			setTaxAmount(next);
			setTotalDisplay(sumItems(form.getFieldValue('items')) + (Number(next) || 0));
		},
		[form, sumItems]
	);

	const handleAddTax = useCallback(() => {
		setHasTax(true);
		setTaxAmount(null);
		setTotalDisplay(sumItems(form.getFieldValue('items')));
	}, [form, sumItems]);

	const handleRemoveTax = useCallback(() => {
		setHasTax(false);
		setTaxAmount(null);
		setTotalDisplay(sumItems(form.getFieldValue('items')));
	}, [form, sumItems]);

	const beforeReceiptUpload = useCallback((file: any) => {
		if (!ACCEPTED_RECEIPT_MIME_TYPES.includes(file.type)) {
			message.error('Receipt must be a PDF or image (PNG or JPG) file');
			return false;
		}
		const isAcceptedSize = file.size / 1024 / 1024 < MAX_RECEIPT_SIZE_MB;
		if (!isAcceptedSize) {
			message.error(`Receipt must be smaller than ${MAX_RECEIPT_SIZE_MB}MB`);
		}
		return isAcceptedSize;
	}, []);

	const handleUploadChange = useCallback(({ file }: any) => {
		setUploading(file.status === 'uploading');
		if (file.status === 'done') {
			const presignedUrl = nullSafeGet('response.data.presignedUrl', file);
			const url =
				nullSafeGet('response.data.fileUrl', file) ||
				nullSafeGet('response.data.url', file) ||
				(presignedUrl ? presignedUrl.split('?')[0] : null);
			if (url) {
				setReceiptUrl(url);
				setReceiptFileName(file.name);
				setReceiptError(false);
			} else {
				message.error(`Upload failed for ${file.name}. Please try again.`);
			}
		}
		if (file.status === 'error') {
			message.error(`Upload failed for ${file.name}`);
		}
	}, []);

	const handleClose = useCallback(() => {
		form.resetFields();
		setReceiptUrl(null);
		setReceiptFileName(null);
		setReceiptError(false);
		setHasTax(initialTaxAmount !== null);
		setTaxAmount(initialTaxAmount);
		onCancel();
	}, [form, onCancel, initialTaxAmount]);

	const receiptRequired = useMemo(
		() =>
			getConfigValueDefaultingToTrue(
				companyConfig,
				'config.spotPurchasesConfig',
				'requireReceiptForSpotPurchases'
			),
		[companyConfig]
	);

	const handleSubmit = useCallback(async () => {
		setReceiptError(false);
		let values: any;
		try {
			values = await form.validateFields();
		} catch (err) {
			return;
		}
		if (receiptRequired && !receiptUrl) {
			setReceiptError(true);
			return;
		}
		setReceiptError(false);
		const items = (values.items || []).map((item: any) => {
			const quantity = Number(item.quantity) || 1;
			const unitCost = Number(item.unitCost) || 0;
			return {
				...(item.id ? { id: item.id } : {}),
				name: (item.description || '').trim(),
				quantity,
				unitCost,
				perUnit: (item.uomLabel || '').trim() || null,
				totalCost: quantity * unitCost,
			};
		});
		const taxValue = hasTax && Number(taxAmount) > 0 ? Number(taxAmount) : null;
		const itemsTotal = items.reduce(
			(acc: number, entry: any) => acc + (Number(entry.totalCost) || 0),
			0
		);
		const payload = {
			...(spotPurchase || {}),
			workOrderId: workOrder.id,
			currencyId,
			receiptUrl,
			receiptFileName,
			items,
			taxAmount: taxValue,
			totalCost: itemsTotal + (taxValue || 0),
		};
		setSubmitting(true);
		try {
			const submit = isUpdate ? update : create;
			await submit(payload);
			message.success(isUpdate ? 'Spot purchase updated' : 'Spot purchase recorded');
			onSuccess();
		} catch (err) {
			message.error(typeof err === 'string' ? err : 'Failed to save spot purchase');
		} finally {
			setSubmitting(false);
		}
	}, [
		form,
		receiptUrl,
		receiptFileName,
		hasTax,
		taxAmount,
		spotPurchase,
		workOrder.id,
		currencyId,
		receiptRequired,
		isUpdate,
		create,
		update,
		onSuccess,
	]);

	const renderUnitPriceInput = (
		<InputNumber
			min={0}
			step={0.01}
			prefix={currencySymbol}
			addonAfter={currencyCode}
			style={{ width: '100%' }}
		/>
	);

	const renderFooter = () => (
		<div className="flex shrink-0 items-center justify-end gap-4 bg-white p-4 shadow-[inset_0_1px_0_0_#f0f0f0]">
			{receiptError ? (
				<span className="text-sm leading-[22px] text-character-danger">Upload Receipt to save</span>
			) : null}
			{receiptViewUrl ? (
				<a
					className="text-base leading-6 text-cyan-7 underline"
					href={receiptViewUrl}
					target="_blank"
					rel="noopener noreferrer"
				>
					view receipt
				</a>
			) : (
				<OWUpload
					accept={ACCEPTED_RECEIPT_TYPES}
					beforeUpload={beforeReceiptUpload}
					onChange={handleUploadChange}
					showUploadList={false}
				>
					<Button
						icon={<UploadOutlined />}
						loading={uploading}
						className={`!h-10 !rounded-sm !px-4 !py-[7.4px] !text-base !leading-6 !shadow-[0_2px_0_rgba(0,0,0,0.02)] ${
							receiptError
								? '!border-character-danger !text-character-danger'
								: '!border-grey-5 !text-character-primary'
						}`}
					>
						{receiptRequired ? <span className="mr-0.5 text-character-danger">*</span> : null}
						Upload Receipt
					</Button>
				</OWUpload>
			)}
			<Button
				onClick={handleSubmit}
				loading={submitting}
				className={`${SAVE_BUTTON_CLASSES} !border-cyan-7 !bg-cyan-7 hover:!border-[#1d5e6d] hover:!bg-[#1d5e6d] hover:!text-white focus:!border-[#1d5e6d] focus:!bg-[#1d5e6d] focus:!text-white`}
			>
				SAVE
			</Button>
		</div>
	);

	return (
		<Modal
			visible={visible}
			onCancel={handleClose}
			closeIcon={<CloseOutlined />}
			title={
				<span className="h4medium text-character-primary">
					{isUpdate ? 'Edit Spot Purchase' : 'New Spot Purchase'}
				</span>
			}
			footer={null}
			width={800}
			wrapClassName="record-spot-purchase-modal"
			bodyStyle={{
				padding: 0,
				display: 'flex',
				flexDirection: 'column',
			}}
		>
			<Form
				form={form}
				layout="vertical"
				initialValues={initialValues}
				onValuesChange={handleValuesChange}
				className="flex min-h-0 flex-1 flex-col"
			>
				<div className="flex min-h-[391px] flex-1 grow flex-col gap-6 overflow-y-auto px-6 py-3">
					<Form.List name="items">
						{(fields, { add, remove }) => (
							<>
								<div className="flex w-full flex-col">
									{fields.map((field, index) => (
										<div
											key={field.key}
											className={`flex w-full flex-col gap-4 ${
												index > 0
													? 'mt-6 border-x-0 border-b-0 border-t border-solid border-[#d9d9d9] pt-6'
													: ''
											}`}
										>
											<div className="flex items-center gap-4">
												<span className="h5medium text-character-primary">Item {index + 1}</span>
												<span className="flex-1 border-x-0 border-b border-t-0 border-dashed border-[#d9d9d9]" />
												<span className="flex items-baseline gap-1">
													<span className="h5regular text-character-primary">
														{(
															(Number(nullSafeGet(`${index}.quantity`, watchedItems)) || 0) *
															(Number(nullSafeGet(`${index}.unitCost`, watchedItems)) || 0)
														).toFixed(2)}
													</span>
													<span className="bodyregular text-character-primary">{currencyCode}</span>
												</span>
												{fields.length > 1 ? (
													<button
														type="button"
														onClick={() => remove(field.name)}
														aria-label="Remove item"
														className="cursor-pointer border-0 bg-transparent p-0 leading-none"
													>
														<DeleteOutlined className="text-[15px] text-cyan-7" />
													</button>
												) : null}
											</div>
											<Form.Item
												{...field}
												name={[field.name, 'description']}
												fieldKey={[field.fieldKey, 'description']}
												label="Name"
												className="!mb-0"
												rules={[
													{
														required: true,
														whitespace: true,
														message: 'Name is required',
													},
												]}
											>
												<Input placeholder="example" />
											</Form.Item>
											<div className="grid w-full grid-cols-3 gap-6">
												<Form.Item
													{...field}
													name={[field.name, 'quantity']}
													fieldKey={[field.fieldKey, 'quantity']}
													label="Quantity"
													className="!mb-0"
													rules={[
														{ required: true, message: 'Quantity is required' },
														{
															type: 'number',
															min: 1,
															message: 'Must be 1 or more',
														},
													]}
												>
													<InputNumber min={1} step={1} style={{ width: '100%' }} />
												</Form.Item>
												<Form.Item
													{...field}
													name={[field.name, 'uomLabel']}
													fieldKey={[field.fieldKey, 'uomLabel']}
													label="Unit"
													className="!mb-0"
													rules={[
														{
															required: true,
															whitespace: true,
															message: 'Unit Required',
														},
													]}
												>
													<Input placeholder="per unit" />
												</Form.Item>
												<Form.Item
													{...field}
													name={[field.name, 'unitCost']}
													fieldKey={[field.fieldKey, 'unitCost']}
													label="Unit Price"
													className="!mb-0"
													rules={[
														{ required: true, message: 'Unit Price is required' },
														{
															type: 'number',
															min: 0,
															message: 'Must be 0 or more',
														},
													]}
												>
													{renderUnitPriceInput}
												</Form.Item>
											</div>
										</div>
									))}
								</div>
								<div className="flex w-full gap-[25px] border-x-0 border-b-0 border-t border-solid border-[#d9d9d9] pt-6">
									<Button
										icon={<PlusOutlined />}
										onClick={() =>
											add({
												description: '',
												quantity: 1,
												uomLabel: DEFAULT_UOM_LABEL,
												unitCost: 0,
											})
										}
									>
										Add Item
									</Button>
									{!hasTax ? (
										<Button icon={<PlusOutlined />} onClick={handleAddTax}>
											Add Tax
										</Button>
									) : null}
								</div>
								{hasTax ? (
									<div className="flex w-full items-center gap-4">
										<span className="h5medium text-character-primary">{`Tax (${currencyCode})`}</span>
										<span className="flex-1 border-x-0 border-b border-t-0 border-dashed border-[#d9d9d9]" />
										<InputNumber
											min={0}
											step={0.01}
											controls={false}
											className="[&_.ant-input-number-input]:!text-right"
											style={{ width: 104 }}
											value={taxAmount === null ? undefined : taxAmount}
											onChange={handleTaxChange}
											formatter={(value) =>
												value === undefined || value === null || value === ''
													? ''
													: `${currencySymbol}${value}`
											}
											parser={(value) => {
												if (!value) return '' as any;
												const cleaned = value.replace(/[^0-9.]/g, '');
												return cleaned as any;
											}}
										/>
										<button
											type="button"
											onClick={handleRemoveTax}
											aria-label="Remove tax"
											className="cursor-pointer border-0 bg-transparent p-0 leading-none"
										>
											<DeleteOutlined className="text-lg text-cyan-7" />
										</button>
									</div>
								) : null}
							</>
						)}
					</Form.List>
				</div>
				<div className="flex shrink-0 items-center gap-4 bg-grey-3 px-6 py-4">
					<span className="h5medium text-character-primary">Total with Tax</span>
					<span className="flex-1 border-x-0 border-b border-t-0 border-dashed border-[#d9d9d9]" />
					<span className="flex items-baseline gap-1">
						<span className="h4medium text-character-primary">{totalDisplay.toFixed(2)}</span>
						<span className="bodyregular text-character-primary">{currencyCode}</span>
					</span>
				</div>
				{renderFooter()}
			</Form>
		</Modal>
	);
};

const mapStateToProps = (state: any) => ({
	currentUser: state.session.currentUser,
	companyConfig: state.company_config.detail,
});

const mapDispatchToProps = (dispatch: any) => ({
	create: (entity: any) => dispatch(spotPurchasesRestCrudThunksForSupplier.create(entity)),
	update: (entity: any) => dispatch(spotPurchasesRestCrudThunksForSupplier.update(entity)),
});

export default connect(mapStateToProps, mapDispatchToProps)(RecordSpotPurchaseModal);
